import { useCallback, useEffect, useState } from 'react'
import { Socket, io } from 'socket.io-client'
import { useLocation } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import { host } from '../../services/bb-api'
import { userLocationsAtom, userStylesAtom } from '../../recoil/atoms'
import { Tooltip } from 'antd'
import styled from 'styled-components'

export let socketInstance: Socket | null = null

export const Users = () => {
	const location = useLocation()
	const [userStyles, setUserStyles] = useRecoilState(userStylesAtom)
	const [userLocations, setUserLocations] = useRecoilState(userLocationsAtom)
	const [localeUsers, setLocaleUsers] = useState<any[]>([])

	const socketHandler = useCallback(
		async (message: any) => {
			if (message.scope === 'user styles') return setUserStyles(message.data)

			if (message.scope === 'location') {
				return setUserLocations((current) => {
					const userLocations = current.filter(({ userId }) => userId !== message.userId)
					userLocations.push(message)
					return userLocations
				})
			}

			if (message.scope === 'connection' && message.action === 'disconnect') {
				return setUserLocations((current) => {
					const userLocations = current.filter(({ userId }) => userId !== message.userId)
					return userLocations
				})
			}

			if (message.scope === 'auth' && message.action === 'unauthorized') {
				socketInstance?.disconnect()
				socketInstance = null
			}
		},
		[setUserStyles, setUserLocations]
	)

	useEffect(() => {
		if (socketInstance === null) {
			const token = localStorage.getItem('bb_token')
			socketInstance = io(host, { withCredentials: true, auth: { token } })
		}

		socketInstance?.on('message', socketHandler)

		return () => {
			socketInstance?.off('message', socketHandler)

			if (socketInstance && socketInstance.connected === false) {
				socketInstance.disconnect()
				socketInstance = null
			}
		}
	}, [socketHandler, location])

	useEffect(() => {
		if (location.pathname === '/auth') {
			socketInstance?.disconnect()
			socketInstance = null
		}

		const emitLocation = () => {
			const message = { scope: 'location', data: location }
			socketInstance?.emit('message', message)
		}

		emitLocation()
		const internal = setInterval(emitLocation, 2500)
		return () => clearInterval(internal)
	}, [location])

	useEffect(() => {
		const userAvatars = document.getElementsByClassName('user-avatar')

		for (const userAvatar of userAvatars) {
			userAvatar.parentNode?.removeChild(userAvatar)
		}

		const localUserLocations = userLocations.filter(({ data }) => {
			return data.pathname.startsWith(location.pathname)
		})

		localUserLocations.forEach(({ data, userId }) => {
			const locationIdPattern = new RegExp(`^${location.pathname}/(?<id>\\d+)`)
			const locationId = locationIdPattern.exec(data.pathname)?.groups?.id ?? null
			if (locationId === null) return

			const tableRow = document.querySelector(`tr[data-row-key="${locationId}"] td`)
			if (tableRow === null) return

			const userStyle = userStyles[userId] ?? null
			if (userStyle === null) return null

			const node = document.createElement(userStyle.imageSource ? 'img' : 'div')
			node.className = 'user-avatar'

			if (userStyle.imageSource) (node as HTMLImageElement).src = userStyle.imageSource
			if (!userStyle.imageSource) node.innerText = userStyle.usernameShort

			node.style.color = userStyle.textColor
			node.style.backgroundColor = userStyle.backgroundColor

			tableRow.appendChild(node)
		})

		const userIds = localUserLocations.map(({ userId }) => userId)
		setLocaleUsers(userIds)
	}, [location, userLocations, userStyles])

	const renderAvatars = useCallback(() => {
		return localeUsers.sort().map((userId, index) => {
			const userStyle = userStyles[userId] ?? null
			if (userStyle === null) return null

			const style = { color: userStyle.textColor, backgroundColor: userStyle.backgroundColor, top: index * -1 + 'rem' }

			return (
				<Tooltip key={userId} title={userStyle.username} placement="left">
					<div className="avatar" style={style}>
						{userStyle.imageSource ? <img src={'/' + userStyle.imageSource} alt="avatar" /> : userStyle.usernameShort}
					</div>
				</Tooltip>
			)
		})
	}, [userStyles, localeUsers])

	if (location.pathname.startsWith('/dashboards')) return null
	return <AvatarsStyled>{socketInstance?.connected && renderAvatars()}</AvatarsStyled>
}

const AvatarsStyled = styled.div`
	position: fixed;
	top: 50%;
	right: 0;
	padding: 1rem;

	display: flex;
	flex-direction: column;
	gap: 0rem;

	transform: translateY(-50%) scale(0.85);
	transition: all 0.2s ease-in-out;

	&:hover {
		transform: translateY(-50%) scale(1);
		padding: 2rem;
		gap: 1.5rem;
	}

	.avatar {
		width: 2rem;
		height: 2rem;

		border-radius: 50%;
		user-select: none;
		transition: all 0.2s ease-in-out;
		position: relative;
		overflow: hidden;

		display: flex;
		align-items: center;
		justify-content: center;

		img {
			height: 80%;
			object-fit: cover;
		}
	}
`
